/** Shared chrome for every slide: a BUD badge, a title, and a short lede. */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface BudRef {
  bud: number;
  pr: number;
  title: string;
}

export const BUDS: Record<number, BudRef> = {
  15: { bud: 15, pr: 104, title: "Client-side CHK encrypted blobs" },
  16: { bud: 16, pr: 105, title: "Directory manifests" },
  17: { bud: 17, pr: 106, title: "Chunked file and directory fanout manifests" },
  18: { bud: 18, pr: 107, title: "Hashtree references" },
};

export function BudBadge({ bud }: { bud: number }) {
  const ref = BUDS[bud];
  if (!ref) return null;

  return (
    <a
      href={`https://github.com/hzrd149/blossom/pull/${ref.pr}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium transition-colors hover:bg-primary/20"
      title={`${ref.title} — PR #${ref.pr}`}
    >
      BUD-{ref.bud}
      <span className="text-muted-foreground">#{ref.pr}</span>
    </a>
  );
}

export function Slide({
  bud,
  title,
  lede,
  children,
  className,
}: {
  bud?: number;
  title: string;
  lede?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-5", className)}>
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {bud !== undefined && <BudBadge bud={bud} />}
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        </div>
        {lede && <p className="max-w-3xl text-sm text-muted-foreground">{lede}</p>}
      </header>
      {children}
    </section>
  );
}

/** A short callout for a rule the spec states explicitly. */
export function SpecNote({
  children,
  tone = "info",
  title,
}: {
  children: ReactNode;
  tone?: "info" | "warn";
  title?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 text-xs",
        tone === "warn"
          ? "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200"
          : "border-border bg-muted/50 text-muted-foreground",
      )}
    >
      {title && <p className="mb-0.5 font-medium">{title}</p>}
      {children}
    </div>
  );
}

/** Section heading inside a slide. */
export function Step({ n, title, children }: { n: number; title: string; children?: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[0.7rem] font-semibold tabular-nums">
          {n}
        </span>
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
      {children}
    </div>
  );
}
